import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, of, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, catchError, map } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { LeadApiService } from '../services/lead-api.service';
import { Lead, LeadRequest, LeadState, LeadCycle, LeadDisqualificationReason } from '../models/lead.model';
import { LeadCloseModalComponent, CloseLeadData } from '../lead-close-modal/lead-close-modal.component';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { ContactCompanyApiService } from '../../empresas/services/contact-company-api.service';
import { Contact } from '../../contactos/models/contacto.model';
import { CompanyResponse, ContactCompanyResponse } from '../../empresas/models/empresa.model';
import { ContactoDrawerService } from '../../contactos/services/contacto-drawer.service';
import { EmpresaDrawerService } from '../../empresas/services/empresa-drawer.service';
import { ToastService } from '../../../shared/services/toast.service';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { CrmUserResponse } from '../../users/models/user.model';

@Component({
  selector: 'app-lead-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    LeadCloseModalComponent,
    EntitySelectComponent
  ],
  templateUrl: './lead-form.component.html'
})
export class LeadFormComponent implements OnInit, OnDestroy {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  leadId: number | null = null;

  // Form model
  name = '';
  idContact: number | null = null;
  idCompany: number | null = null;
  idLeadCycle: number | null = null;
  idLeadState: number | null = null;
  isRetryScheduled = false;
  retryDate = '';

  // ── Responsable (Inc 32) ──
  userPropietyId: number | null = null;
  userPropietyName = '';   // displayText del EntitySelect al editar
  userRegisterName = '';   // creador (solo lectura)

  // Catalogues
  leadStates: LeadState[] = [];
  leadCycles: LeadCycle[] = [];
  disqualificationReasons: LeadDisqualificationReason[] = [];
  loadingCatalogs = false;

  // Close Lead modal (CLOSE_LEAD trigger)
  showCloseLeadModal = false;
  savingCloseLead = false;
  private originalCycleId: number | null = null;
  private pendingCloseData: CloseLeadData | null = null;

  // ── Drawer mode support ──────────────────────────────────────────
  @Input() drawerMode = false;

  @Input() set prefilledContactId(id: number | undefined) { this._prefilledContactId = id; }
  @Input() set prefilledContactName(name: string | undefined) { this._prefilledContactName = name; }
  @Input() set prefilledCompanyId(id: number | undefined) { this._prefilledCompanyId = id; }
  @Input() set prefilledCompanyName(name: string | undefined) { this._prefilledCompanyName = name; }

  @Output() saved = new EventEmitter<Lead>();
  @Output() cancelled = new EventEmitter<void>();

  private _prefilledContactId?: number;
  private _prefilledContactName?: string;
  private _prefilledCompanyId?: number;
  private _prefilledCompanyName?: string;

  // --- Contact typeahead ---
  contactSearchTerm = '';
  contactSearchResults: Contact[] = [];
  showContactDropdown = false;
  searchingContacts = false;
  loadingMoreContacts = false;
  contactSearchPage = 0;
  contactSearchTotalPages = 0;

  // --- Company typeahead ---
  companySearchTerm = '';
  companySearchResults: CompanyResponse[] = [];
  showCompanyDropdown = false;
  searchingCompanies = false;
  loadingMoreCompanies = false;
  companySearchPage = 0;
  companySearchTotalPages = 0;
  companyAutoFilled = false;
  showCompanyChoiceModal = false;
  contactCompaniesForModal: ContactCompanyResponse[] = [];

  // Tracks whether the current name value was auto-generated
  private nameAutoGenerated = false;
  nameAutoFilled = false; // Controls the UI visual flag

  // Duplicate check
  checkingDuplicate = false;
  duplicateError = '';

  private readonly contactSearch$ = new Subject<string>();
  private readonly companySearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private leadApi: LeadApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private contactCompanyApi: ContactCompanyApiService,
    private contactoDrawerService: ContactoDrawerService,
    private empresaDrawerService: EmpresaDrawerService,
    private datetimeService: DatetimeService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarCatalogos();
    this.setupContactSearch();
    this.setupCompanySearch();

    // Escuchar contacto guardado desde el drawer
    this.contactoDrawerService.contactoSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(contact => {
        if (contact?.id) {
          this.idContact = contact.id;
          this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
          this.autoFillName();
        }
      });

    // Escuchar empresa guardada desde el drawer
    this.empresaDrawerService.empresaSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(empresa => {
        if (empresa?.id) {
          this.idCompany = empresa.id;
          this.companySearchTerm = empresa.commercialName;
          this.autoFillName();
        }
      });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.leadId = +id;
      this.cargarLead(this.leadId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Setup typeahead pipes ───────────────────────────────────────────────

  private setupContactSearch(): void {
    this.contactSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        this.contactSearchPage = 0;
        this.contactSearchResults = [];
        this.contactSearchTotalPages = 0;

        if (term.length === 0) {
          this.showContactDropdown = false;
          return of(null);
        }

        this.searchingContacts = true;
        const businessId = this.authService.getCurrentUser()?.businessId || 1;
        return this.contactoApi.searchContacts({ businessId, searchTerm: term, page: 0, size: 10 })
          .pipe(catchError(() => of(null)));
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      this.searchingContacts = false;
      if (response) {
        this.contactSearchResults = response.content;
        this.contactSearchTotalPages = response.totalPages;
        this.showContactDropdown = this.contactSearchResults.length > 0;
      }
    });
  }

  private setupCompanySearch(): void {
    this.companySearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        this.companySearchPage = 0;
        this.companySearchResults = [];
        this.companySearchTotalPages = 0;

        if (term.length === 0) {
          this.showCompanyDropdown = false;
          return of(null);
        }

        this.searchingCompanies = true;
        const businessId = this.authService.getCurrentUser()?.businessId || 1;
        return this.empresaApi.searchCompanies({ businessId, searchTerm: term, page: 0, size: 10 })
          .pipe(catchError(() => of(null)));
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      this.searchingCompanies = false;
      if (response) {
        this.companySearchResults = response.content;
        this.companySearchTotalPages = response.totalPages;
        this.showCompanyDropdown = this.companySearchResults.length > 0;
      }
    });
  }

  // ─── Contact typeahead methods ───────────────────────────────────────────

  onContactSearchInput(term: string): void {
    this.contactSearchTerm = term;
    this.idContact = null;
    this.contactSearch$.next(term);
  }

  onContactSearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactSearchPage = 0;
    this.contactSearchResults = [];
    this.contactSearchTotalPages = 0;
    this.searchingContacts = true;
    this.showContactDropdown = false;

    this.contactoApi.searchContacts({
      businessId,
      searchTerm: this.contactSearchTerm.trim(),
      page: 0,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.searchingContacts = false;
        if (response) {
          this.contactSearchResults = response.content;
          this.contactSearchTotalPages = response.totalPages;
          this.showContactDropdown = this.contactSearchResults.length > 0;
        }
      });
  }

  onContactDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMoreContacts) return;
    if (this.contactSearchPage + 1 >= this.contactSearchTotalPages) return;

    this.loadingMoreContacts = true;
    this.contactSearchPage++;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactoApi.searchContacts({
      businessId,
      searchTerm: this.contactSearchTerm.trim(),
      page: this.contactSearchPage,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.loadingMoreContacts = false;
        if (response) {
          this.contactSearchResults = [...this.contactSearchResults, ...response.content];
          this.contactSearchTotalPages = response.totalPages;
        }
      });
  }

  selectContact(contact: Contact): void {
    this.idContact = contact.id!;
    this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
    this.showContactDropdown = false;
    this.contactSearchResults = [];

    this.idCompany = null;
    this.companySearchTerm = '';
    this.companyAutoFilled = false;
    if (contact.id) {
      const businessId = this.authService.getCurrentUser()?.businessId || 1;
      this.contactCompanyApi.getByContact(contact.id, businessId).pipe(
        catchError(() => of([] as ContactCompanyResponse[]))
      ).subscribe(assocs => {
        if (assocs.length === 1) {
          this.idCompany = assocs[0].idCompany;
          this.companySearchTerm = assocs[0].company?.commercialName || '';
          this.flashCompanyAutoFill();
        } else if (assocs.length > 1) {
          this.contactCompaniesForModal = assocs;
          this.showCompanyChoiceModal = true;
        }
        this.autoFillName();
      });
    } else {
      this.autoFillName();
    }
  }

  closeContactDropdown(): void {
    setTimeout(() => { this.showContactDropdown = false; }, 200);
  }

  // ─── Company typeahead methods ────────────────────────────────────────────

  onCompanySearchInput(term: string): void {
    this.companySearchTerm = term;
    this.idCompany = null;
    this.companySearch$.next(term);
  }

  onCompanySearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.companySearchPage = 0;
    this.companySearchResults = [];
    this.companySearchTotalPages = 0;
    this.searchingCompanies = true;
    this.showCompanyDropdown = false;

    this.empresaApi.searchCompanies({
      businessId,
      searchTerm: this.companySearchTerm.trim(),
      page: 0,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.searchingCompanies = false;
        if (response) {
          this.companySearchResults = response.content;
          this.companySearchTotalPages = response.totalPages;
          this.showCompanyDropdown = this.companySearchResults.length > 0;
        }
      });
  }

  onCompanyDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMoreCompanies) return;
    if (this.companySearchPage + 1 >= this.companySearchTotalPages) return;

    this.loadingMoreCompanies = true;
    this.companySearchPage++;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.empresaApi.searchCompanies({
      businessId,
      searchTerm: this.companySearchTerm.trim(),
      page: this.companySearchPage,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.loadingMoreCompanies = false;
        if (response) {
          this.companySearchResults = [...this.companySearchResults, ...response.content];
          this.companySearchTotalPages = response.totalPages;
        }
      });
  }

  selectCompany(company: CompanyResponse): void {
    const manualName = this.name.trim();

    this.idCompany = company.id;
    this.companySearchTerm = company.commercialName;
    this.showCompanyDropdown = false;
    this.companySearchResults = [];

    // Inc 39: si YA hay un contacto seleccionado, elegir empresa NO debe
    // invalidarlo (antes se ponía idContact=null y solo se recuperaba si la
    // empresa tenía un contacto principal → botón "Crear Lead" quedaba bloqueado).
    if (this.idContact) {
      this.autoFillName();
      if (manualName && !this.nameAutoGenerated) {
        this.name = `${manualName} - ${this.companySearchTerm.trim()}`;
      }
      return;
    }

    const previousContactTerm = '';

    this.idContact = null;
    this.contactSearchTerm = '';
    if (company.id) {
      const businessId = this.authService.getCurrentUser()?.businessId || 1;
      this.contactCompanyApi.getByCompany(company.id, businessId).pipe(
        catchError(() => of([] as ContactCompanyResponse[]))
      ).subscribe(assocs => {
        const primary = assocs.find(a => a.isPrimary) || assocs[0];
        if (primary) {
          const contact = primary.contact;
          if (contact) {
            this.idContact = primary.idContact;
            this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
          }
        }
        if (!this.idContact && previousContactTerm) {
          this.contactSearchTerm = previousContactTerm;
        }
        this.autoFillName();
        if (manualName && !this.nameAutoGenerated) {
          this.name = `${manualName} - ${this.companySearchTerm.trim()}`;
        }
      });
    } else {
      this.autoFillName();
      if (manualName && !this.nameAutoGenerated) {
        this.name = manualName;
      }
    }
  }

  clearCompany(): void {
    this.idCompany = null;
    this.companySearchTerm = '';
    this.autoFillName();
  }

  closeCompanyDropdown(): void {
    setTimeout(() => { this.showCompanyDropdown = false; }, 200);
  }

  private flashCompanyAutoFill(): void {
    this.companyAutoFilled = true;
    setTimeout(() => { this.companyAutoFilled = false; }, 2000);
  }

  selectCompanyInModal(assoc: ContactCompanyResponse): void {
    this.idCompany = assoc.idCompany;
    this.companySearchTerm = assoc.company?.commercialName || '';
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
    this.flashCompanyAutoFill();
    this.autoFillName();
  }

  skipCompanyInModal(): void {
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
  }

  // ─── Catalogues & lead loading ────────────────────────────────────────────

  private cargarCatalogos(): void {
    this.loadingCatalogs = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    Promise.all([
      this.leadApi.getLeadStates(businessId, true).toPromise(),
      this.leadApi.getLeadCycles(businessId, true).toPromise(),
      this.leadApi.getDisqualificationReasons(businessId, true).toPromise()
    ]).then(([states, cycles, reasons]) => {
      this.leadStates = states || [];
      this.leadCycles = cycles || [];
      this.disqualificationReasons = reasons || [];
      this.loadingCatalogs = false;

      if (!this.isEditing) {
        if (this.drawerMode) {
          this.applyPrefill();
        }
        // Auto-select the first state and cycle for new leads
        if (!this.idLeadState && this.leadStates.length > 0) {
          this.idLeadState = this.leadStates[0].id;
        }
        if (!this.idLeadCycle && this.leadCycles.length > 0) {
          this.idLeadCycle = this.leadCycles[0].id;
        }
      }
    }).catch(() => {
      this.loadingCatalogs = false;
    });
  }

  private cargarLead(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.leadApi.getLeadById(id, businessId).subscribe({
      next: (lead: Lead) => {
        this.name = lead.name;
        this.idContact = lead.idContact;
        this.idCompany = lead.idCompany ?? null;
        this.idLeadCycle = lead.idLeadCycle ?? null;
        this.originalCycleId = lead.idLeadCycle ?? null;
        this.idLeadState = lead.idLeadState ?? null;
        this.isRetryScheduled = lead.isRetryScheduled;
        this.retryDate = lead.retryDate ? this.datetimeService.utcToLocalDateInput(lead.retryDate) : '';
        // Inc 32: responsable editable + creador (solo lectura).
        this.userPropietyId = lead.userPropietyId ?? null;
        this.userPropietyName = lead.userPropietyName || '';
        this.userRegisterName = lead.userRegisterName || '';
        this.loading = false;

        // Fetch contact and company names for display
        this.contactoApi.getContactById(lead.idContact)
          .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
          .subscribe(contact => {
            if (contact) {
              this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
            } else {
              this.contactSearchTerm = `Contacto #${lead.idContact}`;
            }
          });

        if (lead.idCompany) {
          this.empresaApi.getCompanyById(lead.idCompany)
            .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
            .subscribe(company => {
              this.companySearchTerm = company ? company.commercialName : `Empresa #${lead.idCompany}`;
            });
        } else {
          this.companySearchTerm = '';
        }
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el lead';
        this.loading = false;
      }
    });
  }

  // ─── Page title ───────────────────────────────────────────────────────────

  get pageTitle(): string {
    return this.isEditing ? 'Editar Lead' : 'Nuevo Lead';
  }

  // Inc 28: no permitir fechas de reintento anteriores a hoy.
  get todayDate(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  get retryDateIsPast(): boolean {
    return !!this.retryDate && this.retryDate < this.todayDate;
  }

  // ─── Auto-fill name ────────────────────────────────────────────────────────

  /**
   * Populates `name` when it is empty or was previously auto-generated.
   * Requires BOTH contact and company (both are mandatory fields).
   * Format: "ContactFirstName - CompanyName"
   */
  autoFillName(): void {
    const manualName = this.name.trim();

    if (!this.contactSearchTerm.trim() && !manualName) {
      return;
    }

    const firstName = this.contactSearchTerm.trim().split(' ')[0] || manualName.split(' - ')[0];

    if (this.idCompany && this.companySearchTerm.trim()) {
      if (manualName && !this.nameAutoGenerated) {
        this.name = `${manualName} - ${this.companySearchTerm.trim()}`;
      } else {
        this.name = `${firstName} - ${this.companySearchTerm.trim()}`;
      }
      this.nameAutoGenerated = true;
      this.flashNameAutoFill();
    } else if (manualName && !this.nameAutoGenerated) {
      this.name = manualName;
    } else if (!manualName && this.contactSearchTerm.trim()) {
      this.name = firstName;
      this.nameAutoGenerated = true;
      this.flashNameAutoFill();
    }
  }

  private flashNameAutoFill(): void {
    this.nameAutoFilled = true;
    setTimeout(() => { this.nameAutoFilled = false; }, 2000);
  }

  /** Call this from the template when user manually edits the name field */
  onNameInput(): void {
    this.nameAutoGenerated = false;
  }

  // ─── Save ─────────────────────────────────────────────────────────────────

  guardar(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const faltantes: string[] = [];
    if (!this.name.trim()) faltantes.push('Nombre');
    if (!this.idContact) faltantes.push('Contacto');
    if (!this.idLeadCycle) faltantes.push('Ciclo de lead');
    if (!this.idLeadState) faltantes.push('Tipo de lead');

    if (faltantes.length > 0) {
      this.error = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    if (this.name.trim().length > 50) {
      this.error = 'El nombre del lead no puede exceder 50 caracteres';
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }
    // Inc 28: bloquear fecha de reintento pasada antes de enviar.
    if (this.isRetryScheduled && this.retryDateIsPast) {
      this.error = 'La fecha de reintento no puede ser anterior a hoy; solo se permiten fechas actuales o futuras.';
      this.toast.warning({ title: 'Fecha no válida', message: this.error });
      return;
    }

    this.error = '';
    this.duplicateError = '';

    // Check for duplicates before saving
this.checkingDuplicate = true;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    const normalizedName = this.name.trim().toLowerCase();

    this.leadApi.searchLeads({ businessId, size: 200 }).pipe(
      map(response => response.content),
      catchError(() => of([] as Lead[])),
      takeUntil(this.destroy$)
    ).subscribe(existingLeads => {
      this.checkingDuplicate = false;

      console.log('Checking duplicates among', existingLeads.length, 'leads');
      console.log('Looking for name:', normalizedName);

      const duplicate = existingLeads.find(lead =>
        lead.name.toLowerCase() === normalizedName &&
        (!this.isEditing || lead.id !== this.leadId)
      );

      console.log('Duplicate found:', duplicate);

      if (duplicate) {
        this.duplicateError = `Ya existe un lead con el mismo nombre, contacto y empresa: "${duplicate.name}"`;
        this.toast.warning({ title: 'Lead duplicado', message: this.duplicateError });
        return;
      }

      this.duplicateError = '';

      // Intercept CLOSE_LEAD trigger before saving
      const cycleChanged = this.isEditing && this.idLeadCycle !== this.originalCycleId;
      if (cycleChanged) {
        const cycle = this.leadCycles.find(c => c.id === this.idLeadCycle);
        if (cycle?.triggerAction === 'CLOSE_LEAD') {
          this.showCloseLeadModal = true;
          return;
        }
      }

      this.executeSave(null);
    });
  }

  confirmarCerrarLead(data: CloseLeadData): void {
    this.showCloseLeadModal = false;
    this.executeSave(data);
  }

  cancelarCerrarLead(): void {
    this.showCloseLeadModal = false;
  }

  private executeSave(closeData: CloseLeadData | null): void {
    this.saving = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: LeadRequest = {
      businessId,
      name: this.name.trim(),
      idContact: this.idContact!,
      idCompany: this.idCompany ?? undefined,
      idLeadCycle: this.idLeadCycle ?? undefined,
      idLeadState: this.idLeadState ?? undefined,
      isRetryScheduled: this.isRetryScheduled,
      retryDate: this.retryDate ? this.datetimeService.dateToUtcIso(this.retryDate) : undefined,
      userPropietyId: this.userPropietyId ?? undefined   // Inc 32
    };

    if (this.isEditing && this.leadId) {
      this.leadApi.updateLead(this.leadId, request).subscribe({
        next: (result: Lead) => {
          const cycleChanged = this.idLeadCycle !== null && this.idLeadCycle !== this.originalCycleId;

          if (!cycleChanged) {
            this.saving = false;
            this.toast.updated('Lead');
            this.navigateAfterSave(result);
            return;
          }

          // El lead base ya se guardó; si una operación secundaria falla,
          // un solo toast de advertencia lo indica (no toast de éxito prematuro).
          const onSecondaryError = (paso: string, err: any) => {
            this.saving = false;
            this.error = err.error?.message || `Error al ${paso}`;
            let aviso = `El lead se actualizó, pero no se pudo ${paso}.`;
            if (err.status === 403) {
              aviso += ' No tienes permisos para realizar esta acción.';
            }
            this.toast.warning({ title: 'Guardado parcial', message: aviso, duration: 8000 });
          };

          const cambiarCiclo = () => {
            this.leadApi.changeCycle(result.id!, this.idLeadCycle!).subscribe({
              next: () => {
                this.saving = false;
                this.toast.updated('Lead');
                this.navigateAfterSave(result);
              },
              error: (err) => onSecondaryError('cambiar el ciclo', err)
            });
          };

          if (closeData) {
            this.leadApi.disqualifyLead(
              result.id!,
              businessId,
              closeData.disqualificationReasonId,
              closeData.disqualificationNotes,
              closeData.retryDate,
              closeData.isRetryScheduled || undefined
            ).subscribe({
              next: () => cambiarCiclo(),
              error: (err) => onSecondaryError('descalificar el lead', err)
            });
          } else {
            cambiarCiclo();
          }
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el lead';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.leadApi.createLead(request).subscribe({
        next: (result: Lead) => {
          this.saving = false;
          this.navigateAfterSave(result);
          this.toast.created('Lead');
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el lead';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  get leadForModal(): Partial<Lead> {
    return { name: this.name };
  }

  private navigateAfterSave(result: Lead): void {
    if (this.drawerMode) {
      this.saved.emit(result);
    } else {
      this.router.navigate(['/leads']);
    }
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/leads']);
    }
  }

  /** Called by drawer before opening */
  reset(): void {
    this.isEditing = false;
    this.leadId = null;
    this.name = '';
    this.nameAutoGenerated = false;
    this.nameAutoFilled = false;
    this.idContact = null;
    this.idCompany = null;
    this.idLeadCycle = null;
    this.originalCycleId = null;
    this.idLeadState = null;
    this.isRetryScheduled = false;
    this.retryDate = '';
    this.userPropietyId = null;
    this.userPropietyName = '';
    this.userRegisterName = '';
    this.contactSearchTerm = '';
    this.companySearchTerm = '';
    this.contactSearchResults = [];
    this.companySearchResults = [];
    this.error = '';
    this.saving = false;
    this.companyAutoFilled = false;
    this.showCompanyChoiceModal = false;
  }

  /** Called by drawer in edit mode */
  loadLead(id: number): void {
    this.isEditing = true;
    this.leadId = id;
    this.cargarLead(id);
  }

  abrirNuevoContacto(): void {
    this.contactoDrawerService.open();
  }

  abrirNuevaEmpresa(): void {
    this.empresaDrawerService.open();
  }

  // ── Buscador de responsable (Inc 32) — client-side: el endpoint de usuarios
  //    no filtra por texto, se filtra en cliente sobre nombre/correo. ──
  userSearch = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.leadApi.getUsersByBusiness(businessId).pipe(
      map((users) => {
        const t = term.toLowerCase();
        const items = t
          ? users.filter(u => `${u.fullname} ${u.lastname ?? ''} ${u.mail}`.toLowerCase().includes(t))
          : users;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 }))
    );
  };

  userLabel = (u: CrmUserResponse): string => `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  onOwnerChange(userId: number | null): void {
    this.userPropietyId = userId;
  }

  private applyPrefill(): void {
    if (this._prefilledContactId) {
      this.idContact = this._prefilledContactId;
      this.contactSearchTerm = this._prefilledContactName || `Contacto #${this._prefilledContactId}`;
    }
    if (this._prefilledCompanyId) {
      this.idCompany = this._prefilledCompanyId;
      this.companySearchTerm = this._prefilledCompanyName || `Empresa #${this._prefilledCompanyId}`;
    }
    this.autoFillName();
  }
}
